// lib/digest/wheel-groups.ts
//
// The Digest wheel card's content (FE-DIGEST-1 Phase D3b1). One pure adapter
// turns what the page already receives (an edition payload and the watch
// surface) into five groups in wheel order, one per event type, each with the
// items the detail column steps through and the line its pill shows.
//
// PURE. No React, no fetch, no clock: today's date is passed in, so the same
// input always gives the same output and the test can pin any day.
//
// HONESTY RULES this file holds (Paper mapping note 2OIZ-0, board log):
//   - Nothing is invented. A phase, a title or a drug is shown only when the
//     surface or the payload carries it.
//   - Backend text is shown as written apart from case. ALL-CAPS registry
//     words are put in sentence case, nothing is reworded.
//   - A month-only date stays a month ("November 2026"), never a padded day.
//   - A date type is "estimate" only for ESTIMATED or ANTICIPATED, "actual"
//     only for ACTUAL, and "not stated" for anything else.
//   - Preprints carry no summary. One fixed sentence per link phrase plus
//     the shared "Preprint, not peer reviewed" label.
//   - Dates are formatted by hand in en-GB order from UTC parts, never with
//     the browser's locale.
//
// Replaced when BE-DIGEST-EVENTS-1 sends the wheel's list from the server.
//
// Phase D3c adds `groupTodos`, which groups the edition's to-do list into the
// board's five columns by the same event types.

import { PREPRINT_LABEL_TEXT } from "@/components/tracker/preprint-label"
import type {
  DigestEditionPayload,
  DigestPreprintBlock,
  DigestPreprintLine,
  DigestReceipt,
  DigestStoryPayload,
} from "@/lib/digest/edition-schema"
import type { UpcomingReadout } from "@/lib/pipeline-watch/landscapes"

// ---- Types ----

export type WheelTypeId = "research" | "readout" | "indication" | "company" | "trial"

/** Wheel order, top to bottom (Paper 2O9T-0 and the prototype's slot table). */
export const WHEEL_ORDER: readonly WheelTypeId[] = [
  "research",
  "readout",
  "indication",
  "company",
  "trial",
]

export const WHEEL_TYPE_NAMES: Record<WheelTypeId, string> = {
  research: "Research update",
  readout: "Upcoming readout",
  indication: "Indication / class",
  company: "Company / SEC",
  trial: "Trial change",
}

/** The part of the watch surface the wheel reads. Both lists optional. */
export type WheelSurface = {
  /** The fields read from `surface.trials` (a WatchTrial fits). */
  trials?: ReadonlyArray<{
    nctId: string
    monitored?: { briefTitle?: string | null; phases?: readonly string[] | null } | null
  }> | null
  upcomingReadouts?: readonly UpcomingReadout[] | null
}

/** Who the header shows. The card draws a drug as its sphere and every
 *  other kind (none, a preprint server, the type) as the type's icon in the
 *  shadowed chip (round 4). The initials stay in the data. */
export type WheelIdentity =
  | { kind: "drug"; drug: string }
  | { kind: "none" }
  | { kind: "initials"; text: string }
  | { kind: "type" }
  | { kind: "logo"; src: string }

export type WheelFactRow = {
  label: string
  value: string
  /** Internal route (the trial page) or an external URL. */
  href?: string | null
  external?: boolean
}

export type WheelSource = { label: string; href: string }

/** One steppable item in the detail column. */
export type WheelItem = {
  key: string
  nctId: string | null
  identity: WheelIdentity
  name: string
  subLine: string
  /** The date row's text, or null for no date row. */
  date: string | null
  headline: string
  /** Paragraphs of the scrolling text. */
  body: string[]
  rows: WheelFactRow[]
  /** Null when there is no link to give ("Source link not available"). */
  source: WheelSource | null
}

/** What the column says when a type has nothing to step through. */
export type WheelEmpty = {
  identity: WheelIdentity
  name: string
  subLine: string
  headline: string
  body: string
  /** Coming soon shows the type line under the header (2OFE-0). The quiet
   *  states do not (2P7H-0). */
  showTypeLine: boolean
  rows: WheelFactRow[]
}

export type WheelGroup = {
  type: WheelTypeId
  name: string
  status: "live" | "coming_soon"
  /** The pill's small line when not selected, and when selected. */
  pillLine: string
  activePillLine: string
  items: WheelItem[]
  /** Set instead of items when the block is grouped (more than five). */
  grouped: WheelItem | null
  empty: WheelEmpty | null
}

export type WheelModel = {
  groups: WheelGroup[]
  openingType: WheelTypeId
}

export type WheelInput = {
  payload: DigestEditionPayload | null
  surface: WheelSurface | null
  isBackIssue: boolean
  /** Today in the edition calendar, yyyy-mm-dd (UTC). */
  today: string
}

// ---- Dates (en-GB order, UTC parts, by hand) ----

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

type IsoParts = { y: number; m: number; d: number | null }

/** "2026-10-12" or "2026-11". Anything else is null, never guessed. */
function parseIsoDate(v: string | null | undefined): IsoParts | null {
  const match = /^(\d{4})-(\d{2})(?:-(\d{2}))?$/.exec(String(v ?? "").trim())
  if (!match) return null
  const y = Number(match[1])
  const m = Number(match[2])
  const d = match[3] ? Number(match[3]) : null
  if (m < 1 || m > 12) return null
  if (d !== null && (d < 1 || d > 31)) return null
  return { y, m, d }
}

/** Milliseconds at UTC midnight. A month-only date counts from the 1st, as
 *  the live page's window does (digest-page.tsx readoutsInWindow). */
function utcDay(p: IsoParts): number {
  return Date.UTC(p.y, p.m - 1, p.d ?? 1)
}

/** "12 Oct" for a day, "November 2026" for a month. No padded day. */
function formatShort(p: IsoParts): string {
  return p.d === null ? `${MONTHS_LONG[p.m - 1]} ${p.y}` : `${p.d} ${MONTHS_SHORT[p.m - 1]}`
}

/** "12 Oct 2026" for a day, "November 2026" for a month. */
function formatFull(p: IsoParts): string {
  return p.d === null ? `${MONTHS_LONG[p.m - 1]} ${p.y}` : `${p.d} ${MONTHS_SHORT[p.m - 1]} ${p.y}`
}

/** "12 October 2026" for a day, "November 2026" for a month. */
function formatLong(p: IsoParts): string {
  return p.d === null ? `${MONTHS_LONG[p.m - 1]} ${p.y}` : `${p.d} ${MONTHS_LONG[p.m - 1]} ${p.y}`
}

/** The readout headline's date: "12 October" (Paper 2O67-0) or "November
 *  2026" for a month. */
function formatHeadlineDate(p: IsoParts): string {
  return p.d === null ? `${MONTHS_LONG[p.m - 1]} ${p.y}` : `${p.d} ${MONTHS_LONG[p.m - 1]}`
}

// ---- Text helpers ----

/** ALL-CAPS backend text in sentence case ("ACTIVE NOT RECRUITING" ->
 *  "Active not recruiting", "2 OCT" -> "2 Oct"). Text holding any lower-case
 *  letter is the writer's own casing and is returned unchanged. */
function sentenceCase(text: string): string {
  const t = text.trim()
  if (!/[A-Z]/.test(t) || /[a-z]/.test(t)) return t
  const lower = t.toLowerCase()
  const i = lower.search(/[a-z]/)
  const sentence =
    i < 0 ? lower : lower.slice(0, i) + lower.charAt(i).toUpperCase() + lower.slice(i + 1)
  return restoreProperCase(sentence)
}

/** Month names, short and long, keyed lower case. */
const MONTH_TOKENS: Record<string, string> = Object.fromEntries(
  [...MONTHS_SHORT, ...MONTHS_LONG, "Sept"].map((m) => [m.toLowerCase(), m]),
)

/** Words that keep their own casing after sentence case: month names
 *  ("jul 2026" -> "Jul 2026", review 1 item 1) and registry numbers
 *  ("nct01234567" -> "NCT01234567"). "may" is a month only next to a number,
 *  so an ordinary "may" stays a word. */
function restoreProperCase(text: string): string {
  return text.replace(/[a-z0-9]+/gi, (word, offset: number, all: string) => {
    const lower = word.toLowerCase()
    if (/^nct\d+$/.test(lower)) return word.toUpperCase()
    const month = MONTH_TOKENS[lower]
    if (!month) return word
    if (lower === "may") {
      const before = all.slice(0, offset).trimEnd()
      const after = all.slice(offset + word.length).trimStart()
      if (!/\d$/.test(before) && !/^\d/.test(after)) return word
    }
    return month
  })
}

/** "dapagliflozin" -> "Dapagliflozin", as track-copy's displayName. */
function displayDrug(drug: string): string {
  return drug.charAt(0).toUpperCase() + drug.slice(1)
}

/** "PHASE2" -> "Phase 2", several joined with "/". NA and absent phases give
 *  null, never a label that says nothing. Copied from digest-page.tsx as it
 *  is, so "EARLY_PHASE1" stays raw exactly as the live page shows it. */
function phaseLabel(phases: readonly string[] | null | undefined): string | null {
  const real = (phases ?? []).filter((p) => p && p !== "NA")
  if (real.length === 0) return null
  return real
    .map((p) => p.replace(/^PHASE/, "Phase ").replace(/\s+/g, " ").trim())
    .join("/")
}

/** Registry overall statuses, compared with spaces, case folded. */
const REGISTRY_STATUSES = new Set([
  "NOT YET RECRUITING",
  "RECRUITING",
  "ENROLLING BY INVITATION",
  "ACTIVE NOT RECRUITING",
  "ACTIVE, NOT RECRUITING",
  "SUSPENDED",
  "TERMINATED",
  "COMPLETED",
  "WITHDRAWN",
  "UNKNOWN",
  "UNKNOWN STATUS",
  "AVAILABLE",
  "NO LONGER AVAILABLE",
  "TEMPORARILY NOT AVAILABLE",
  "APPROVED FOR MARKETING",
  "WITHHELD",
])

function isRegistryStatus(text: string): boolean {
  return REGISTRY_STATUSES.has(text.trim().toUpperCase().replace(/_/g, " ").replace(/\s+/g, " "))
}

/** Only a registry number gets a registry link or a trial page. The sample
 *  edition's story ids ("lead", "s2") get neither. */
function isNctId(v: string | null | undefined): v is string {
  return typeof v === "string" && /^NCT\d{8}$/.test(v)
}

function ctgovHref(nctId: string): string {
  return `https://clinicaltrials.gov/study/${encodeURIComponent(nctId)}`
}

/** The internal trial page, as the live page's openTrial builds it. */
function trialHref(nctId: string): string {
  return `/tools/trial-tracker/trials/${encodeURIComponent(nctId)}`
}

function isHttpUrl(v: string | null | undefined): v is string {
  return typeof v === "string" && /^https?:\/\//i.test(v.trim())
}

// ---- Trial change ----

/** The rail node the backend marked "detected", as written but in sentence
 *  case ("2 OCT" -> "2 Oct"). Null when the rail has none. */
function detectedDate(story: DigestStoryPayload): string | null {
  const node = story.rail.find((n) => n.state === "detected")
  return node ? sentenceCase(node.date) : null
}

/** Prose segments joined, claim segments by their text. Real editions write
 *  whole sentences per segment (joined with a space), the sample edition
 *  writes fragments that carry their own spacing and punctuation (joined as
 *  they are). */
function storyText(story: DigestStoryPayload): string {
  let out = ""
  for (const seg of story.prose) {
    const text = typeof seg === "string" ? seg : seg.text
    if (!text) continue
    const glue = out === "" || /\s$/.test(out) || /^[\s,.;:!?)]/.test(text) ? "" : " "
    out += glue + text
  }
  return out.replace(/\s+/g, " ").trim()
}

/** A receipt as a fact row, or null for kinds the column does not show.
 *  pills: label from the head ("DETECTED · 2 OCT" becomes "Status" when both
 *  sides are registry statuses, otherwise "Changed"), value "{from} to {to}".
 *  chip: "Changed" and its text. bars: skipped. */
function receiptRow(r: DigestReceipt): WheelFactRow | null {
  const head = r.head?.text?.trim() ?? ""
  if (r.body.kind === "pills") {
    const from = sentenceCase(r.body.from.text)
    const to = sentenceCase(r.body.to.text)
    let label: string
    if (!head || /^DETECTED\b/i.test(head)) {
      label =
        head && isRegistryStatus(r.body.from.text) && isRegistryStatus(r.body.to.text)
          ? "Status"
          : "Changed"
    } else {
      label = sentenceCase(head)
    }
    return { label, value: from ? `${from} to ${to}` : to }
  }
  if (r.body.kind === "chip") {
    return { label: "Changed", value: sentenceCase(r.body.text) }
  }
  return null
}

function trialLookup(
  surface: WheelSurface | null,
  nctId: string,
): { phase: string | null; briefTitle: string | null } {
  const t = surface?.trials?.find((x) => x.nctId === nctId)
  if (!t) return { phase: null, briefTitle: null }
  return { phase: phaseLabel(t.monitored?.phases), briefTitle: t.monitored?.briefTitle ?? null }
}

/** Header lines for a trial: the drug as name and phase plus trial number
 *  below, or the trial number as name when no drug is linked. */
function trialHeader(
  drug: string | null,
  nctId: string,
  phase: string | null,
): { identity: WheelIdentity; name: string; subLine: string } {
  const nct = isNctId(nctId) ? nctId : null
  if (drug) {
    return {
      identity: { kind: "drug", drug },
      name: displayDrug(drug),
      subLine: [phase, nct].filter(Boolean).join(" · ") || "ClinicalTrials.gov",
    }
  }
  return {
    identity: { kind: "none" },
    name: nct ?? "Trial",
    subLine: phase ?? "ClinicalTrials.gov",
  }
}

function trialChangeItem(story: DigestStoryPayload, surface: WheelSurface | null, i: number): WheelItem {
  const { phase, briefTitle } = trialLookup(surface, story.id)
  const date = detectedDate(story)
  const fromReceipts = story.receipts
    .map(receiptRow)
    .filter((r): r is WheelFactRow => r !== null)
    .slice(0, 2)
  const rows: WheelFactRow[] =
    fromReceipts.length > 0
      ? fromReceipts
      : [
          isNctId(story.id)
            ? { label: "Trial", value: briefTitle ?? story.id, href: trialHref(story.id) }
            : { label: "Trial", value: briefTitle ?? "Not given" },
          ...(date ? [{ label: "Detected", value: date }] : []),
        ]
  return {
    key: `trial-${i}-${story.id}`,
    nctId: story.id,
    ...trialHeader(story.drug, story.id, phase),
    date,
    headline: story.headline,
    body: [storyText(story)].filter(Boolean),
    rows,
    source: isNctId(story.id)
      ? { label: "ClinicalTrials.gov record", href: ctgovHref(story.id) }
      : null,
  }
}

function trialGroup(payload: DigestEditionPayload | null, surface: WheelSurface | null): WheelGroup {
  const stories = payload ? [...(payload.lead ? [payload.lead] : []), ...payload.stories] : []
  const items = stories.map((s, i) => trialChangeItem(s, surface, i))
  const first = items[0]
  return {
    type: "trial",
    name: WHEEL_TYPE_NAMES.trial,
    status: "live",
    pillLine: first ? (first.date ?? "ClinicalTrials.gov") : "Nothing new tonight",
    activePillLine: first
      ? first.date
        ? `ClinicalTrials.gov · ${first.date}`
        : "ClinicalTrials.gov"
      : "Nothing new tonight",
    items,
    grouped: null,
    empty: items.length
      ? null
      : {
          identity: { kind: "type" },
          name: WHEEL_TYPE_NAMES.trial,
          subLine: "ClinicalTrials.gov",
          headline: "Nothing moved tonight",
          body: "No trial in this watch changed in a way worth writing up.",
          showTypeLine: false,
          rows: [],
        },
  }
}

// ---- Upcoming readout ----

type DateTypeWord = "actual" | "estimate" | "not stated"

/** ACTUAL is actual, ESTIMATED and ANTICIPATED are estimates, anything else
 *  (null included) is not stated. Never guessed. */
function dateTypeWord(type: string | null | undefined): DateTypeWord {
  const t = String(type ?? "").trim().toUpperCase()
  if (t === "ACTUAL") return "actual"
  if (t === "ESTIMATED" || t === "ANTICIPATED") return "estimate"
  return "not stated"
}

function readoutsInWindow(
  all: readonly UpcomingReadout[],
  today: string,
): { inWindow: Array<{ r: UpcomingReadout; p: IsoParts }>; nextBeyond: { r: UpcomingReadout; p: IsoParts } | null } {
  const t = parseIsoDate(today)
  if (!t || t.d === null) return { inWindow: [], nextBeyond: null }
  const start = utcDay(t)
  const horizon = start + 30 * 86_400_000
  const dated = all
    .map((r) => ({ r, p: parseIsoDate(r.date) }))
    .filter((x): x is { r: UpcomingReadout; p: IsoParts } => x.p !== null)
    .map((x, i) => ({ ...x, i, at: utcDay(x.p) }))
    // Soonest first, ties in the surface's own order.
    .sort((a, b) => a.at - b.at || a.i - b.i)
  const inWindow = dated.filter((x) => x.at >= start && x.at <= horizon)
  const beyond = dated.find((x) => x.at > horizon) ?? null
  return {
    inWindow: inWindow.map(({ r, p }) => ({ r, p })),
    nextBeyond: beyond ? { r: beyond.r, p: beyond.p } : null,
  }
}

function readoutBody(word: DateTypeWord, p: IsoParts): string {
  const on = p.d === null ? `in ${formatLong(p)}` : `on ${formatLong(p)}`
  if (word === "estimate") {
    return `The sponsor's registry entry estimates primary completion ${on}. The date is an estimate and has not been confirmed.`
  }
  if (word === "actual") {
    return `The sponsor's registry entry gives primary completion ${on} and marks the date as actual.`
  }
  return `The sponsor's registry entry lists primary completion ${on}. It does not say whether the date is actual or an estimate.`
}

function readoutItem(r: UpcomingReadout, p: IsoParts, i: number): WheelItem {
  const word = dateTypeWord(r.type)
  const phase = phaseLabel(r.phases)
  const suffix = word === "not stated" ? "" : ` · ${word}`
  return {
    key: `readout-${i}-${r.nctId}`,
    nctId: r.nctId,
    ...trialHeader(r.drug, r.nctId, phase),
    date: `${formatFull(p)}${suffix}`,
    headline: `Primary completion due ${formatHeadlineDate(p)}`,
    body: [readoutBody(word, p)],
    rows: [
      { label: "Date type", value: word.charAt(0).toUpperCase() + word.slice(1) },
      { label: "Trial", value: r.briefTitle ?? r.nctId, href: trialHref(r.nctId) },
    ],
    source: { label: "ClinicalTrials.gov record", href: ctgovHref(r.nctId) },
  }
}

function readoutGroup(surface: WheelSurface | null, isBackIssue: boolean, today: string): WheelGroup {
  const base = {
    type: "readout" as const,
    name: WHEEL_TYPE_NAMES.readout,
    status: "live" as const,
    grouped: null,
  }
  const emptyBase = {
    identity: { kind: "type" } as const,
    name: WHEEL_TYPE_NAMES.readout,
    subLine: "ClinicalTrials.gov",
    showTypeLine: false,
    rows: [],
  }
  if (isBackIssue) {
    return {
      ...base,
      pillLine: "Today's edition only",
      activePillLine: "Today's edition only",
      items: [],
      empty: {
        ...emptyBase,
        headline: "Today's edition only",
        body: "Upcoming readouts are shown on today's edition, from the registry as it reads now.",
      },
    }
  }
  const { inWindow, nextBeyond } = readoutsInWindow(surface?.upcomingReadouts ?? [], today)
  if (inWindow.length === 0) {
    let body = "No trial on this watch carries a completion date ahead."
    if (nextBeyond) {
      const word = dateTypeWord(nextBeyond.r.type)
      const suffix = word === "not stated" ? "" : `, ${word === "estimate" ? "an estimate" : "actual"}`
      body = `The next primary completion date on this watch is ${formatFull(nextBeyond.p)}${suffix}.`
    }
    return {
      ...base,
      pillLine: "Nothing due in 30 days",
      activePillLine: "Nothing due in 30 days",
      items: [],
      empty: { ...emptyBase, headline: "Nothing due in 30 days", body },
    }
  }
  const items = inWindow.map(({ r, p }, i) => readoutItem(r, p, i))
  const first = inWindow[0]
  const suffix = dateTypeWord(first.r.type) === "estimate" ? " · estimate" : ""
  return {
    ...base,
    pillLine: `${formatShort(first.p)}${suffix}`,
    activePillLine: `Registry · ${formatFull(first.p)}${suffix}`,
    items,
    empty: null,
  }
}

// ---- Research update ----

/** One fixed sentence per link phrase. No summary, no abstract. */
const PREPRINT_SENTENCES: Record<string, string> = {
  "reports results": "This preprint reports results from a trial on your watch.",
  "uses trial data": "This preprint uses data from a trial on your watch.",
  protocol: "This preprint describes the protocol of a trial on your watch.",
  "mentions this trial": "This preprint mentions a trial on your watch.",
}
const PREPRINT_SENTENCE_OTHER = "This preprint is linked to a trial on your watch."

function preprintSentence(linkPhrase: string): string {
  return PREPRINT_SENTENCES[linkPhrase.trim().toLowerCase()] ?? PREPRINT_SENTENCE_OTHER
}

/** "medRxiv" -> "mR", "bioRxiv" -> "bR" (Paper 2ODJ-0). Other servers get
 *  their first two letters. */
function serverInitials(server: string): string {
  const s = server.trim()
  if (/rxiv$/i.test(s)) return `${s.charAt(0).toLowerCase()}R`
  return s.slice(0, 2)
}

/** "Reports results" -> "reports results", for "Preprint · reports results". */
function lowerFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1)
}

function preprintItem(line: DigestPreprintLine, i: number): WheelItem {
  const p = parseIsoDate(line.postedDate)
  const rows: WheelFactRow[] = [
    {
      label: "Linked trial",
      value: line.acronym ? `${line.acronym} · ${line.nctId}` : line.nctId,
      href: trialHref(line.nctId),
    },
    { label: "Status", value: line.withdrawn ? "Withdrawn" : PREPRINT_LABEL_TEXT },
  ]
  if (line.journalDoi) {
    rows.push({
      label: "Published",
      value: line.journalDoi,
      href: isHttpUrl(line.journalUrl) ? line.journalUrl : null,
      external: true,
    })
  }
  const body = [`${preprintSentence(line.headline)} ${PREPRINT_LABEL_TEXT}.`]
  if (line.withdrawn) body.push(`${line.server} lists this preprint as withdrawn.`)
  if (line.registryNote) body.push(line.registryNote)
  return {
    key: `preprint-${i}-${line.nctId}-${line.url}`,
    nctId: line.nctId,
    identity: { kind: "initials", text: serverInitials(line.server) },
    name: line.server,
    subLine: `Preprint · ${lowerFirst(line.headline)}`,
    date: p ? formatFull(p) : line.postedDate,
    headline: line.title,
    body,
    rows,
    source: isHttpUrl(line.url) ? { label: `${line.server} preprint`, href: line.url } : null,
  }
}

function groupedItem(block: DigestPreprintBlock): WheelItem | null {
  const g = block.grouped
  if (!g) return null
  const from = parseIsoDate(block.fromDate)
  const to = parseIsoDate(block.toDate)
  const range =
    from && to
      ? block.fromDate === block.toDate
        ? formatFull(from)
        : `${formatFull(from)} to ${formatFull(to)}`
      : null
  return {
    key: "preprints-grouped",
    nctId: null,
    identity: { kind: "type" },
    // The breakdown counts link types, not servers, so no server is named.
    name: WHEEL_TYPE_NAMES.research,
    subLine: "Preprints",
    date: range,
    headline: `${g.count} preprints mention your watched trials in this update`,
    body: [`More than five arrived in this window, so they are counted here rather than listed. ${PREPRINT_LABEL_TEXT}.`],
    rows: g.breakdown.map((b) => ({ label: b.headline, value: String(b.count) })),
    source: null,
  }
}

function researchGroup(payload: DigestEditionPayload | null): WheelGroup {
  const block = payload?.preprints ?? null
  const base = { type: "research" as const, name: WHEEL_TYPE_NAMES.research, status: "live" as const }
  const grouped = block ? groupedItem(block) : null
  if (grouped && block?.grouped) {
    const line = `${block.grouped.count} preprints`
    return { ...base, pillLine: line, activePillLine: line, items: [], grouped, empty: null }
  }
  const items = (block?.items ?? []).map(preprintItem)
  if (items.length === 0) {
    return {
      ...base,
      pillLine: "Nothing new tonight",
      activePillLine: "Nothing new tonight",
      items: [],
      grouped: null,
      empty: {
        identity: { kind: "type" },
        name: WHEEL_TYPE_NAMES.research,
        subLine: "bioRxiv and medRxiv",
        headline: "Nothing new tonight",
        body: "No preprint linked to a trial on this watch arrived in this edition.",
        showTypeLine: false,
        rows: [],
      },
    }
  }
  // The newest posted date. yyyy-mm-dd compares as text. Ties keep the first.
  const lines = block!.items
  let newest = lines[0]
  for (const l of lines) if (l.postedDate > newest.postedDate) newest = l
  const p = parseIsoDate(newest.postedDate)
  return {
    ...base,
    pillLine: p ? formatShort(p) : newest.postedDate,
    activePillLine: `${newest.server} · ${p ? formatFull(p) : newest.postedDate}`,
    items,
    grouped: null,
    empty: null,
  }
}

// ---- Coming soon ----

const COMING_SOON_BODY: Record<"indication" | "company", string> = {
  indication: "Updates to indications and drug classes on your watch will appear here.",
  company: "Sponsor releases and SEC filings for companies on your watch will appear here.",
}

function comingSoonGroup(type: "indication" | "company"): WheelGroup {
  return {
    type,
    name: WHEEL_TYPE_NAMES[type],
    status: "coming_soon",
    pillLine: "Coming soon",
    activePillLine: "Coming soon",
    items: [],
    grouped: null,
    empty: {
      identity: { kind: "type" },
      name: WHEEL_TYPE_NAMES[type],
      subLine: "Not available yet",
      headline: "Coming soon",
      body: COMING_SOON_BODY[type],
      showTypeLine: true,
      rows: [],
    },
  }
}

// ---- Main ----

/** Trial change when it has stories, otherwise Research update when it has
 *  preprints, otherwise Trial change. Readouts do not count as new tonight. */
function openingType(groups: Record<WheelTypeId, WheelGroup>): WheelTypeId {
  if (groups.trial.items.length > 0) return "trial"
  if (groups.research.items.length > 0 || groups.research.grouped) return "research"
  return "trial"
}

export function buildWheelGroups(input: WheelInput): WheelModel {
  const { payload, surface, isBackIssue, today } = input
  const byType: Record<WheelTypeId, WheelGroup> = {
    research: researchGroup(payload),
    readout: readoutGroup(surface, isBackIssue, today),
    indication: comingSoonGroup("indication"),
    company: comingSoonGroup("company"),
    trial: trialGroup(payload, surface),
  }
  return { groups: WHEEL_ORDER.map((t) => byType[t]), openingType: openingType(byType) }
}

// ---- To-do list (FE-DIGEST-1 Phase D3c) ----
//
// The edition's merged checklist (`todoList`, built on the server by
// lib/digest/todo-merge.ts) grouped into the board's five columns. The list
// is taken as the server sends it: nothing is filtered, reordered or dated
// here, so the counts and the cards can never disagree with the read.

/** Column order on the board, left to right (Paper 2LIE-0). Not the wheel's
 *  order. */
export const TODO_COLUMN_ORDER: readonly WheelTypeId[] = [
  "trial",
  "company",
  "indication",
  "readout",
  "research",
]

/** Each to-do type's wheel type (the live page's grouping, digest-sheet.tsx
 *  TYPE_LABEL). Nothing feeds Indication / class or Research update yet. */
const TODO_TYPE_TO_WHEEL: Record<string, WheelTypeId> = {
  amendment: "trial",
  date_move: "trial",
  results: "trial",
  stop: "trial",
  new_trial: "trial",
  enrollment: "trial",
  readout_prep: "readout",
  announcement: "company",
}

/** The type's label, as the live page's chip reads (digest-sheet.tsx). The
 *  board does not draw it: it goes into the checkbox's accessible name. */
export const TODO_TYPE_LABELS: Record<string, string> = {
  amendment: "Amendment",
  date_move: "Date move",
  results: "Results",
  stop: "Stop",
  new_trial: "New trial",
  enrollment: "Enrollment",
  readout_prep: "Readout",
  announcement: "Announcement",
}

function isKnownTodoType(type: string): boolean {
  return Object.prototype.hasOwnProperty.call(TODO_TYPE_TO_WHEEL, type)
}

/** The column for a to-do type. A type this file does not know is put in
 *  Trial change rather than dropped, so a new server type stays visible and
 *  is counted. `groupTodos` reports it in `unknownTypes`. */
export function todoWheelType(type: string): WheelTypeId {
  return isKnownTodoType(type) ? TODO_TYPE_TO_WHEEL[type] : "trial"
}

/** The label for a type, or the raw type when it is unknown (as the live
 *  page does). */
export function todoTypeLabel(type: string): string {
  return TODO_TYPE_LABELS[type] ?? type
}

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

/** "2026-08-18" -> "Tue 18", from UTC parts in en-GB order, so the
 *  browser's locale and time zone cannot change the day. Anything else is
 *  returned as given. */
function todoShortDay(date: string): string {
  const p = parseIsoDate(date)
  if (!p || p.d === null) return date
  const d = new Date(Date.UTC(p.y, p.m - 1, p.d))
  if (Number.isNaN(d.getTime())) return date
  return `${DAYS_SHORT[d.getUTCDay()]} ${d.getUTCDate()}`
}

/** The date a to-do card shows. An item first raised before this edition
 *  reads "from {origin}" (as the live page's carried rows do). That covers
 *  carried items and also NEW items whose origin is earlier, because the
 *  merge gives a re-raised item the origin of its unbroken chain
 *  (lib/digest/todo-merge.ts). An item first raised in this edition shows
 *  the edition's own date. yyyy-mm-dd compares as text. */
export function todoDateText(
  item: { isNew: boolean; originEditionDate: string },
  editionDate: string,
): string {
  const earlier = item.originEditionDate < editionDate
  return !item.isNew || earlier
    ? `from ${todoShortDay(item.originEditionDate)}`
    : todoShortDay(editionDate)
}

/** A column's items split for the board (round 5, Oliver 2026-10-03
 *  10:21Z): open items first, then the ticked ones under "Done", each part
 *  in the list's own order. Unticking an item puts it back in its place
 *  among the open ones, because the order is the list's, not the tick's. */
export function splitDone<T extends { checked: boolean }>(
  items: readonly T[],
): { open: T[]; done: T[] } {
  return {
    open: items.filter((i) => !i.checked),
    done: items.filter((i) => i.checked),
  }
}

export type TodoColumn<T> = {
  type: WheelTypeId
  name: string
  /** The column's items in the list's own order. */
  items: T[]
  count: number
}

export type TodoBoardModel<T> = {
  /** Always five, in TODO_COLUMN_ORDER. */
  columns: TodoColumn<T>[]
  total: number
  done: number
  /** Types not in the known mapping, each once, in first-seen order. */
  unknownTypes: string[]
}

export function groupTodos<T extends { type: string; checked: boolean }>(
  list: readonly T[],
): TodoBoardModel<T> {
  const byType = new Map<WheelTypeId, T[]>(TODO_COLUMN_ORDER.map((t) => [t, []]))
  const unknownTypes: string[] = []
  let done = 0
  for (const item of list) {
    if (!isKnownTodoType(item.type) && !unknownTypes.includes(item.type)) {
      unknownTypes.push(item.type)
    }
    byType.get(todoWheelType(item.type))!.push(item)
    if (item.checked) done += 1
  }
  return {
    columns: TODO_COLUMN_ORDER.map((type) => {
      const items = byType.get(type)!
      return { type, name: WHEEL_TYPE_NAMES[type], items, count: items.length }
    }),
    total: list.length,
    done,
    unknownTypes,
  }
}

/** The closing line of the narrow to-do list (FE-DIGEST-1 Phase D4c1, Paper
 *  2QNV-0). On a narrow sheet the empty columns are hidden and this one line
 *  names them, in column order: "Nothing to follow up for Indication / class
 *  or Research update." No empty column: no line. Every column empty: no line
 *  either, because the board then shows its quiet line instead of columns. */
export function todoEmptyTypesLine(
  columns: ReadonlyArray<{ name: string; count: number }>,
): string | null {
  const empty = columns.filter((c) => c.count === 0).map((c) => c.name)
  if (empty.length === 0 || empty.length === columns.length) return null
  const names =
    empty.length === 1
      ? empty[0]
      : `${empty.slice(0, -1).join(", ")} or ${empty[empty.length - 1]}`
  return `Nothing to follow up for ${names}.`
}

export const _internals = {
  isKnownTodoType,
  todoShortDay,
  todoWheelType,
  todoTypeLabel,
  TODO_TYPE_TO_WHEEL,
  parseIsoDate,
  formatShort,
  formatFull,
  formatLong,
  formatHeadlineDate,
  sentenceCase,
  phaseLabel,
  isRegistryStatus,
  dateTypeWord,
  readoutsInWindow,
  receiptRow,
  storyText,
  detectedDate,
  serverInitials,
  preprintSentence,
  trialHref,
  ctgovHref,
  isNctId,
  restoreProperCase,
}
